import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import styled from "styled-components";

import Layout from "layout";
import Card from "components/card";
import Loader from "components/loader";
import Select from "components/select";
import Button from "components/button";
import { t } from "intl/index";
import { getLangSettings } from "utils/language";
import useAppStore from "stores/appStore";

const Wrapper = styled.div`
  text-align: center;
  max-width: 400px;
  margin: 0 auto;
`;

const SelectWrapper = styled.div`
  margin: 1.5rem 0;
  text-align: left;
`;

const ButtonWrapper = styled.div`
  margin-top: 1.5rem;
`;

const ErrorMessage = styled.p`
  color: #d32f2f;
  margin: 1rem 0;
`;

const defaultHeaderSettings = {
  primaryColor: "#1976d2",
  secondaryColor: "#dc004e",
  logoBase64: "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7",
};

const NameSelectionScreen = ({ surveyId }) => {
  const { lang } = useAppStore();
  const effectiveLang = lang || getLangSettings();
  const LANG_MAP = t(effectiveLang);

  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedParticipant, setSelectedParticipant] = useState([]);

  useEffect(() => {
    const fetchParticipants = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data: versionData } = await axios.get(
          `${process.env.REACT_APP_FASTIFY_API_URL}/v1/version`
        );
        const version = versionData.version;

        const { data } = await axios.get(
          `${process.env.REACT_APP_FASTIFY_API_URL}/${version}/surveys/${surveyId}/participants/unclaimed`
        );
        setParticipants(data.participants || []);
      } catch (err) {
        if (err?.response?.status === 404) {
          setError(LANG_MAP.QR_SURVEY_NOT_FOUND);
        } else {
          setError(LANG_MAP.QR_LOAD_ERROR);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchParticipants();
  }, [surveyId]);

  const handleContinue = () => {
    const participantId = selectedParticipant[0]?.value;
    if (!participantId) return;
    sessionStorage.setItem("surveyQrFlow", "1");
    const url = new URL(window.location);
    url.searchParams.set("surveyId", surveyId);
    url.searchParams.set("hash", participantId);
    url.searchParams.delete("qr");
    window.location.href = url.toString();
  };

  const participantOptions = participants.map((participant) => ({
    label: participant.displayValue ?? participant.display_value ?? participant.name,
    value: participant.id,
  }));

  if (loading) {
    return <Loader title={LANG_MAP.LOADING} />;
  }

  if (error) {
    return (
      <Layout settings={defaultHeaderSettings}>
        <Card>
          <Wrapper>
            <ErrorMessage>{error}</ErrorMessage>
          </Wrapper>
        </Card>
      </Layout>
    );
  }

  if (participants.length === 0) {
    return (
      <Layout settings={defaultHeaderSettings}>
        <Card>
          <Wrapper>
            <h3>{LANG_MAP.QR_SELECT_TITLE}</h3>
            <p>{LANG_MAP.QR_NO_PARTICIPANTS}</p>
          </Wrapper>
        </Card>
      </Layout>
    );
  }

  return (
    <Layout settings={defaultHeaderSettings}>
      <Card>
        <Wrapper>
          <h3>{LANG_MAP.QR_SELECT_TITLE}</h3>
          <SelectWrapper>
            <Select
              options={participantOptions}
              value={selectedParticipant}
              onChange={setSelectedParticipant}
              onInputChange={() => {}}
              isMulti={false}
              placeholder={LANG_MAP.QR_SELECT_PLACEHOLDER}
            />
          </SelectWrapper>
          <ButtonWrapper>
            <Button
              onClick={handleContinue}
              title={LANG_MAP.NEXT_BUTTON}
              isDisabled={selectedParticipant.length === 0}
            />
          </ButtonWrapper>
        </Wrapper>
      </Card>
    </Layout>
  );
};

NameSelectionScreen.propTypes = {
  surveyId: PropTypes.string.isRequired,
};

export default NameSelectionScreen;
